import React, { useContext, useEffect } from 'react';

import * as PetitionFormStyled from '../EveryActionPetitionForm.styles';

import * as Styled from './DetailsStepContent.styles';

import * as FormStyled from '+/styles/styledComponents/Form.styles';
import { Button } from '+/components/Button';
import { Link } from '+/components/Link';
import { NameSplitter } from '+/components/NameSplitter';
import { Tooltip } from '+/components/Tooltip';
import { LanguageContext } from '+/contexts/LanguageContext';
import { initializeStateWithCookieVal, useDisplayRememberMe } from '+/contexts/RememberMe';
import FormikNameController from '+/utils/FormikNameController';
import { t } from '+/utils/textDictionary';
import { useCountries } from '+/utils/hooks/useCountries';
import { ICONS } from '+/utils/constants/icons';

export const DetailsStepContent = ({
  onMessageStep,
  includeMessageStep,
  errors,
  values,
  setFieldValue,
  handleChange,
  handleBlur,
  touched,
  isSubmitting,
  handleSubmit,
  handleClickNext,
  displayFormError,
  isAdvocacyForm,
  rememberMeChecked,
  updateRememberMeChecked,
  isUS,
  displaySmsOptIn,
  suppliedOptions,
}: DetailsStepContentProps) => {
  const controller = new FormikNameController({ values, setFieldValue });
  const countriesList = useCountries();
  const language: string = useContext(LanguageContext);

  const displayRememberMe = useDisplayRememberMe();

  useEffect(() => {
    if (isUS) {
      updateRememberMeChecked(true);
    } else {
      initializeStateWithCookieVal(() => updateRememberMeChecked(true));
    }
  }, [isUS]);

  const handleRememberMeChange = () => {
    updateRememberMeChecked(!rememberMeChecked);
  };

  return (
    <Styled.Content
      initial={false}
      animate={{
        opacity: onMessageStep ? 0 : 1,
        visibility: 'visible',
        transitionEnd: { visibility: onMessageStep ? 'hidden' : 'visible' },
      }}
      transition={{ duration: 0.15, ease: 'easeOut' }}
    >
      <FormStyled.CustomFieldRow style={{ flexWrap: 'wrap', justifyContent: 'space-between' }}>
        {typeof values.Prefix !== 'undefined' && (
          <FormStyled.InputLabel
            style={{ width: '25%', flexGrow: 1, flexBasis: '15%' }}
            floating
            errored={!!errors.Prefix}
          >
            <FormStyled.TextyInput
              name="Prefix"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.Prefix}
              autoComplete="Prefix"
              placeholder={t('EAPetitionForm.Prefix')}
            />
            <FormStyled.InputLabelText floating>
              {t('EAPetitionForm.Prefix')}
            </FormStyled.InputLabelText>
            {touched.Prefix && errors.Prefix && (
              <FormStyled.InputError>{errors.Prefix}</FormStyled.InputError>
            )}
          </FormStyled.InputLabel>
        )}
        <FormStyled.InputLabel
          style={{ width: '35%', flexGrow: 1, flexBasis: '50%' }}
          floating
          errored={!!errors.FirstName}
        >
          <FormStyled.TextyInput
            name="FirstName"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.FirstName}
            autoComplete="given-name"
            placeholder={`${t('EAPetitionForm.FirstName')}*`}
          />
          <FormStyled.InputLabelText floating>
            {t('EAPetitionForm.FirstName')}
            <sup>*</sup>
          </FormStyled.InputLabelText>
          {touched.FirstName && errors.FirstName && (
            <FormStyled.InputError>{errors.FirstName}</FormStyled.InputError>
          )}
          <NameSplitter
            controller={controller}
            namePartToCheck="FirstName"
            namePartToSplit="Prefix"
            position="start"
            nameLabel={t('EAPetitionForm.Prefix')}
          />
          <NameSplitter
            controller={controller}
            namePartToCheck="FirstName"
            namePartToSplit="MiddleName"
            position="end"
            nameLabel={t('EAPetitionForm.MiddleName')}
          />
        </FormStyled.InputLabel>
        {typeof values.MiddleName !== 'undefined' && values.MiddleName && (
          <FormStyled.InputLabel
            style={{ width: '45%', flexGrow: 1, flexBasis: '25%' }}
            floating
            errored={!!errors.MiddleName}
          >
            <FormStyled.TextyInput
              name="MiddleName"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.MiddleName}
              autoComplete="given-name"
              placeholder={t('EAPetitionForm.MiddleName')}
            />
            <FormStyled.InputLabelText floating>
              {t('EAPetitionForm.MiddleName')}
            </FormStyled.InputLabelText>
            {touched.MiddleName && errors.MiddleName && (
              <FormStyled.InputError>{errors.MiddleName}</FormStyled.InputError>
            )}
          </FormStyled.InputLabel>
        )}
        <FormStyled.InputLabel
          style={{ width: '45%', flexGrow: 1 }}
          floating
          errored={!!errors.LastName}
        >
          <FormStyled.TextyInput
            name="LastName"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.LastName}
            autoComplete="family-name"
            placeholder={`${t('EAPetitionForm.LastName')}*`}
          />
          <NameSplitter
            controller={controller}
            namePartToCheck="LastName"
            namePartToSplit="MiddleName"
            position="start"
            nameLabel={t('EAPetitionForm.MiddleName')}
          />
          <NameSplitter
            controller={controller}
            namePartToCheck="LastName"
            namePartToSplit="Suffix"
            position="end"
            nameLabel={t('EAPetitionForm.Suffix')}
          />
          <FormStyled.InputLabelText floating>
            {t('EAPetitionForm.LastName')}
            <sup>*</sup>
          </FormStyled.InputLabelText>
          {touched.LastName && errors.LastName && (
            <FormStyled.InputError>{errors.LastName}</FormStyled.InputError>
          )}
        </FormStyled.InputLabel>
        {typeof values.Suffix !== 'undefined' && values.Suffix && (
          <FormStyled.InputLabel
            style={{ width: '25%', flexGrow: 1, flexBasis: '15%' }}
            floating
            errored={!!errors.Suffix}
          >
            <FormStyled.TextyInput
              name="Suffix"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.Suffix}
              autoComplete="suffix"
              placeholder={t('EAPetitionForm.Suffix')}
            />
            <FormStyled.InputLabelText floating>
              {t('EAPetitionForm.Suffix')}
            </FormStyled.InputLabelText>
            {touched.Suffix && errors.Suffix && (
              <FormStyled.InputError>{errors.Suffix}</FormStyled.InputError>
            )}
          </FormStyled.InputLabel>
        )}
      </FormStyled.CustomFieldRow>
      <FormStyled.CustomFieldRow>
        <FormStyled.InputLabel fullWidth floating errored={!!errors.EmailAddress}>
          <FormStyled.TextyInput
            name="EmailAddress"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.EmailAddress}
            autoComplete="email"
            placeholder={`${t('EAPetitionForm.EmailAddress')}*`}
          />
          <FormStyled.InputLabelText floating>
            {t('EAPetitionForm.EmailAddress')}
            <sup>*</sup>
          </FormStyled.InputLabelText>
          {touched.EmailAddress && errors.EmailAddress && (
            <FormStyled.InputError>{errors.EmailAddress}</FormStyled.InputError>
          )}
        </FormStyled.InputLabel>
      </FormStyled.CustomFieldRow>
      {language !== 'es-MX' && (
        <FormStyled.CustomFieldRow>
          <FormStyled.InputLabel fullWidth floating errored={!!errors.MobilePhone}>
            <FormStyled.TextyInput
              name="MobilePhone"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.MobilePhone}
              autoComplete="tel"
              placeholder={t('EAPetitionForm.MobilePhone')}
            />
            <FormStyled.InputLabelText floating>
              {t('EAPetitionForm.MobilePhone')}
            </FormStyled.InputLabelText>
            {touched.MobilePhone && errors.MobilePhone && (
              <FormStyled.InputError>{errors.MobilePhone}</FormStyled.InputError>
            )}
          </FormStyled.InputLabel>
        </FormStyled.CustomFieldRow>
      )}
      <FormStyled.CustomFieldRow>
        <FormStyled.InputLabel floating errored={!!errors.PostalCode} style={{ flexBasis: '60%' }}>
          <FormStyled.TextyInput
            name="PostalCode"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.PostalCode}
            autoComplete="postal-code"
            placeholder={`${t('EAPetitionForm.PostalCode')}${isUS ? '*' : ''}`}
          />
          <FormStyled.InputLabelText floating>
            {t('EAPetitionForm.PostalCode')}
            {isUS && <sup>*</sup>}
          </FormStyled.InputLabelText>
          {touched.PostalCode && errors.PostalCode && (
            <FormStyled.InputError>{errors.PostalCode}</FormStyled.InputError>
          )}
        </FormStyled.InputLabel>
        <FormStyled.InputLabel floating errored={!!errors.Country}>
          <FormStyled.Select
            name="Country"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.Country || ''}
            autoComplete="country"
            required
          >
            <option value="" disabled>
              {t('EAPetitionForm.Country')}
            </option>
            {(suppliedOptions.table_countries && language === 'en-US'
              ? suppliedOptions.table_countries
              : countriesList
            ).map(o => (
              <option key={o.value} value={o.value}>
                {o.display}
              </option>
            ))}
          </FormStyled.Select>
          <FormStyled.InputLabelText floating>
            {t('EAPetitionForm.Country')}
            <sup>*</sup>
          </FormStyled.InputLabelText>
          {touched.Country && errors.Country && (
            <FormStyled.InputError>{errors.Country}</FormStyled.InputError>
          )}
        </FormStyled.InputLabel>
      </FormStyled.CustomFieldRow>
      <FormStyled.CustomFieldRow style={{ marginTop: '1.25rem' }}>
        <FormStyled.CheckboxLabel>
          <FormStyled.Checkbox
            type="checkbox"
            name="YesSignMeUpForUpdatesForBinder"
            value={values.YesSignMeUpForUpdatesForBinder ? 1 : 0}
            onChange={event => {
              setFieldValue('YesSignMeUpForUpdatesForBinder', event.target.checked);
            }}
            onBlur={handleBlur}
            checked={!!values.YesSignMeUpForUpdatesForBinder}
          />
          <FormStyled.CheckboxLabelText>
            {t('EAPetitionForm.Consent')}
            <Tooltip
              content={t('EAPetitionForm.InfoConsent')}
              teaser={t('EAPetitionForm.InfoConsent')}
              inline
            />
          </FormStyled.CheckboxLabelText>
          {touched.YesSignMeUpForUpdatesForBinder && !!errors.YesSignMeUpForUpdatesForBinder && (
            /* eslint-enable indent */
            <FormStyled.InputError>{errors.YesSignMeUpForUpdatesForBinder}</FormStyled.InputError>
            /* eslint-disable indent */
          )}
        </FormStyled.CheckboxLabel>
      </FormStyled.CustomFieldRow>
      {displaySmsOptIn && (
        <FormStyled.CustomFieldRow>
          <FormStyled.CheckboxLabel>
            <FormStyled.Checkbox
              type="checkbox"
              name="SmsSubscribeMobilePhone"
              value={values.SmsSubscribeMobilePhone ? 1 : 0}
              onChange={event => {
                setFieldValue('SmsSubscribeMobilePhone', event.target.checked);
              }}
              onBlur={handleBlur}
              checked={!!values.SmsSubscribeMobilePhone}
            />
            <FormStyled.CheckboxLabelText>
              {t('EAPetitionForm.ConsentSms')}
            </FormStyled.CheckboxLabelText>
            {touched.SmsSubscribeMobilePhone && !!errors.SmsSubscribeMobilePhone && (
              <FormStyled.InputError>{errors.SmsSubscribeMobilePhone}</FormStyled.InputError>
            )}
          </FormStyled.CheckboxLabel>
        </FormStyled.CustomFieldRow>
      )}
      {displayRememberMe && (
        <FormStyled.CustomFieldRow>
          <FormStyled.CheckboxLabel>
            <FormStyled.Checkbox
              type="checkbox"
              name="RememberMeAction"
              value={rememberMeChecked ? 1 : 0}
              onChange={handleRememberMeChange}
              checked={rememberMeChecked}
            />
            <FormStyled.CheckboxLabelText>
              Remember my details{' '}
              <Tooltip
                teaser="Save your address details"
                content="Safely remember your name, email, phone number, and mailing address to take action faster next time! Your personal information will be encrypted and stored securely in your browser. Uncheck this box if you prefer to re-enter your details each time."
                inline
              />
            </FormStyled.CheckboxLabelText>
          </FormStyled.CheckboxLabel>
        </FormStyled.CustomFieldRow>
      )}
      {!isUS && (
        <FormStyled.CustomFieldRow>
          <FormStyled.CheckboxLabel>
            <FormStyled.Checkbox
              type="checkbox"
              name="AcceptedPrivacyPolicy"
              value={values.AcceptedPrivacyPolicy ? 1 : 0}
              onChange={event => {
                setFieldValue('AcceptedPrivacyPolicy', event.target.checked);
              }}
              onBlur={handleBlur}
              checked={!!values.AcceptedPrivacyPolicy}
            />
            <FormStyled.CheckboxLabelText>
              {t('EAPetitionForm.AcceptPrivacyPolicyPrefix')}{' '}
              <Styled.Link as={Link} href={t('EAPetitionForm.AcceptPrivacyPolicyLinkUrl')}>
                {t('EAPetitionForm.AcceptPrivacyPolicyLinkText')}
              </Styled.Link>
              {t('EAPetitionForm.AcceptPrivacyPolicySuffix')}
            </FormStyled.CheckboxLabelText>
            {touched.AcceptedPrivacyPolicy && !!errors.AcceptedPrivacyPolicy && (
              <FormStyled.InputError>{errors.AcceptedPrivacyPolicy}</FormStyled.InputError>
            )}
          </FormStyled.CheckboxLabel>
        </FormStyled.CustomFieldRow>
      )}
      <PetitionFormStyled.ButtonRow centered>
        <PetitionFormStyled.ButtonContainer>
          {includeMessageStep ? (
            <PetitionFormStyled.SubmitButton>
              <Button
                narrow
                big
                endIcon={ICONS.CARET_RIGHT}
                disabled={isSubmitting}
                onClick={handleClickNext}
              >
                {t('EAAdvocacyForm.ReviewMessage')}
              </Button>
            </PetitionFormStyled.SubmitButton>
          ) : (
            <PetitionFormStyled.SubmitButton>
              <Button narrow big type="submit" disabled={isSubmitting} onClick={handleSubmit}>
                {isAdvocacyForm ? t('EAAdvocacyForm.SendMessage') : t('EAPetitionForm.SignNow')}
              </Button>
            </PetitionFormStyled.SubmitButton>
          )}
        </PetitionFormStyled.ButtonContainer>
        {displayFormError && (
          <FormStyled.InputError>{t('EAPetitionForm.FormError')}</FormStyled.InputError>
        )}
      </PetitionFormStyled.ButtonRow>
    </Styled.Content>
  );
};
